import { skipToken, type QueryClient } from '@tanstack/react-query'
import { trpc } from '@/main'
import { deriveProfiles } from './use-root-search'
import type { RootSearch } from '@/routes/__root'

type ReportInput = Exclude<Parameters<typeof trpc.reports.getReport.queryOptions>[0], typeof skipToken>

// The ONLY search keys the server `getReport` consumes (mirror of `Profiles` in apps/shared). The
// report query key MUST be built from JUST these — never the whole search object. Spreading all of
// `search` (q, p, the *Sort params, pageType, …) into the input made `q` part of the query key, so
// the report refetched (running its loading bar) on every list-search keystroke even though its data
// doesn't depend on `q`. List/render-only params stay out of the key; the report is now stable.
const REPORT_INPUT_KEYS = [
  'first',
  'second',
  'selected',
  'type',
  'orgType',
  'teamId',
  'evaluation',
  'members',
  'name',
] as const satisfies readonly (keyof ReportInput)[]

export function reportEnabled(profiles: { first: unknown; second: unknown }) {
  if (!profiles.first && !profiles.second) return skipToken
  const input = {} as ReportInput
  for (const key of REPORT_INPUT_KEYS) {
    const value = (profiles as Record<string, unknown>)[key]
    if (value !== undefined) (input as Record<string, unknown>)[key] = value
  }
  return input
}

/**
 * SINGLE SOURCE of the `getReport` query options, referenced by the loader prefetch
 * (`prefetchReport`) AND the component (`ReportLoaded`) — so the streamed key lines up with the
 * component's `useSuspenseQuery` (route query co-location convention, see AGENTS.md). The input
 * derivation itself is already shared via `deriveProfiles` + `reportEnabled`; this wraps the one
 * `trpc.reports.getReport.queryOptions(...)` call so it lives in a single place.
 */
export function reportQueryOptions(input: ReportInput) {
  return trpc.reports.getReport.queryOptions(input)
}

export function prefetchReport(
  queryClient: QueryClient,
  search: RootSearch,
  params: Parameters<typeof deriveProfiles>[1],
  isReportPage = false,
  isMarketPage = false,
) {
  const profiles = deriveProfiles(search, params, isReportPage, 'search', isMarketPage)
  const input = reportEnabled({ ...search, ...profiles })
  // No profile selected (e.g. /market before picking one): nothing to fetch — the component renders
  // its empty/selection state.
  if (input === skipToken) return undefined
  // Return the promise so a loader can AWAIT it on the server (complete SSR HTML, no streaming skeleton
  // on reload). prefetchQuery never rejects, so awaiting it is crash-safe.
  return queryClient.prefetchQuery(reportQueryOptions(input))
}
